import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Button, Switch, message, Spin } from 'antd';
import { ArrowLeftOutlined, EditOutlined, FilePdfOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessibleTrip, tripWebHref, updateTrip } from '../../services/tripsService';
import { useCanonicalTripUrl } from '../../utils/useCanonicalTripUrl';
import { loadWeatherForCityDay } from '../../services/weatherOrchestrator';
import {
  loadLocalUserPreferences,
  patchAppUserSettings,
} from '../../services/userPreferencesSyncService';
import type { Trip } from '../../types/client';
import type { ForecastDay } from '../../types/weather';
import {
  startPollingTripPlans,
} from '../../services/aiPlanStatusService';
import { subscribeAiPlanJobsChanged } from '../../services/aiPlanJobService';
import { weatherEn } from '../../content/weather.en';
import { tripDetailEn } from '../../content/trips.en';
import { tripShareEn } from '../../content/tripShare.en';
import { resolveTripTemperatureUnit } from '../../utils/temperatureUnit';
import { mapWithConcurrency } from '../../utils/asyncPool';
import CruiseCountdownCard from '../../components/CruiseCountdownCard/CruiseCountdownCard';
import TripChecklistDrawer from '../../components/TripChecklistDrawer/TripChecklistDrawer';
import NotesAttachmentsCard from '../../components/NotesAttachmentsCard/NotesAttachmentsCard';
import CityVisitCard from '../../components/CityVisitCard/CityVisitCard';
import TripShareRoster from '../../components/TripShareRoster/TripShareRoster';
import { AccommodationIcon } from '../../components/icons/TripIcons';
import {
  buildTripDayCards,
  defaultShowPlanIcons,
  enumerateStayDays,
  formatTripHeaderDate,
} from '../../utils/tripDayCards';
import { isPastVisitDate } from '../../utils/preferenceFilters';
import { buildItineraryPdfModel } from '../../utils/itineraryPdfModel';
import { findDailyInCache, getWeatherCache } from '../../services/weatherCacheStore';
import {
  renderItineraryPdf,
  shareOrDownloadItineraryPdf,
} from '../../services/itineraryPdf';
import { isAtSeaLocation } from '../../services/cruiseItinerariesApi';
import {
  canOpenAttachments,
  canShowShareUI,
  effectiveCanEdit,
} from '../../utils/tripAccessPolicy';
import styles from './TripDetail.module.css';

/** Cap parallel Open-Meteo loads on long cruises (e.g. 175 ports). */
const TRIP_DETAIL_WEATHER_CONCURRENCY = 4;

const TripDetail: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { user, isDataSyncing } = useAuth();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPlanIcons, setShowPlanIcons] = useState(() => {
    const prefs = loadLocalUserPreferences().appUserSettings;
    return prefs.showPlanIcons ?? true;
  });
  const [hidePastCities, setHidePastCities] = useState(
    () => loadLocalUserPreferences().appUserSettings.hidePastCities
  );
  const [hidePastDays, setHidePastDays] = useState(
    () => loadLocalUserPreferences().appUserSettings.hidePastDays
  );
  const [weatherByKey, setWeatherByKey] = useState<Record<string, ForecastDay | null>>({});
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useCanonicalTripUrl(trip);

  const tripHref = user?.sub && trip ? tripWebHref(user.sub, trip) : trip ? `/trips/${trip.id}` : '/trips';

  const reload = useCallback((): void => {
    if (!user?.sub || !tripId) {
      setTrip(null);
      setLoading(false);
      return;
    }
    const next = getAccessibleTrip(user.sub, tripId);
    setTrip(next);
    const prefs = loadLocalUserPreferences().appUserSettings;
    setHidePastCities(prefs.hidePastCities);
    setHidePastDays(prefs.hidePastDays);
    if (next) {
      setShowPlanIcons(
        prefs.showPlanIcons != null ? prefs.showPlanIcons : defaultShowPlanIcons(next)
      );
    }
    setLoading(false);
  }, [user?.sub, tripId]);

  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isDataSyncing) reload();
  }, [isDataSyncing, reload]);

  useEffect(() => {
    if (!tripId || !trip) return;

    const startPolls = (): void => {
      startPollingTripPlans(trip.id, {
        onTripUpdated: () => {
          reloadRef.current();
        },
      });
    };

    startPolls();

    const unsubscribe = subscribeAiPlanJobsChanged((changedTripId) => {
      if (changedTripId.trim().toLowerCase() !== trip.id.trim().toLowerCase()) return;
      reloadRef.current();
      startPolls();
    });

    return () => {
      unsubscribe();
      // Do not stopAllCityPlanPolling — kickoff-started polls for other trips
      // (and background completion for this trip) must keep running after leave.
    };
  }, [tripId, trip?.id]);

  const handleShowPlanIconsChange = (checked: boolean): void => {
    setShowPlanIcons(checked);
    patchAppUserSettings({ showPlanIcons: checked });
  };

  const persist = (updates: Partial<Omit<Trip, 'id'>>): void => {
    if (!user?.sub || !trip) return;
    const updated = updateTrip(user.sub, trip.id, updates);
    if (updated) setTrip(updated);
  };

  const handleToggleTask = (taskId: string): void => {
    if (!trip) return;
    const taskItems = (trip.taskItems ?? []).map((t) =>
      t.id === taskId ? { ...t, isDone: !t.isDone } : t
    );
    persist({ taskItems });
  };

  const handleDeleteTask = (taskId: string): void => {
    if (!trip) return;
    persist({ taskItems: (trip.taskItems ?? []).filter((t) => t.id !== taskId) });
  };

  const handleAddTask = (title: string): void => {
    if (!trip) return;
    const existing = trip.taskItems ?? [];
    const nextOrder =
      existing.reduce((max, t) => Math.max(max, t.sortOrder), -1) + 1;
    persist({
      taskItems: [
        ...existing,
        {
          id: crypto.randomUUID(),
          title,
          isDone: false,
          sortOrder: nextOrder,
        },
      ],
    });
  };

  const dayCards = useMemo(() => (trip ? buildTripDayCards(trip) : []), [trip]);
  const visibleDayCards = useMemo(() => {
    if (!hidePastCities && !hidePastDays) return dayCards;
    return dayCards.filter((c) => {
      if (hidePastCities && isPastVisitDate(c.departureDate, c.arrivalDate)) {
        return false;
      }
      if (hidePastDays) {
        const stayDays = enumerateStayDays(c.arrivalDate, c.departureDate);
        if (stayDays.length === 0) return !isPastVisitDate(c.departureDate, c.arrivalDate);
        return stayDays.some((ymd) => !isPastVisitDate(ymd, ymd));
      }
      return true;
    });
  }, [dayCards, hidePastCities, hidePastDays]);
  const temperatureUnit = trip ? resolveTripTemperatureUnit(trip) : 'F';

  const loadWeatherForCards = useCallback(
    async (force = false): Promise<void> => {
      if (!user?.sub || !trip) return;

      const loads: { cityId: string; date: string }[] = [];
      for (const card of visibleDayCards) {
        if (card.isAtSea || !card.cityId) continue;
        let days = enumerateStayDays(card.arrivalDate, card.departureDate);
        if (hidePastDays) {
          days = days.filter((ymd) => !isPastVisitDate(ymd, ymd));
        }
        for (const date of days) {
          loads.push({ cityId: card.cityId, date });
        }
      }
      if (loads.length === 0) return;

      setWeatherLoading(true);
      try {
        await mapWithConcurrency(loads, TRIP_DETAIL_WEATHER_CONCURRENCY, async (load) => {
          const key = `${load.cityId}_${load.date}`;
          const coords = trip.cityCoords?.[load.cityId];
          const result = await loadWeatherForCityDay({
            userId: user.sub,
            cityVisitId: load.cityId,
            lat: coords?.latitude,
            lng: coords?.longitude,
            date: load.date,
            force,
          });
          setWeatherByKey((prev) => ({ ...prev, [key]: result.day }));
        });
      } finally {
        setWeatherLoading(false);
      }
    },
    [user?.sub, trip, visibleDayCards, hidePastDays]
  );

  useEffect(() => {
    void loadWeatherForCards();
  }, [loadWeatherForCards]);
  const attachmentCityOptions = useMemo(
    () =>
      dayCards
        .filter((c) => !isAtSeaLocation(c.cityName))
        .reduce<{ id: string; name: string }[]>((acc, c) => {
          if (acc.some((x) => x.id === c.cityId)) return acc;
          acc.push({ id: c.cityId, name: c.cityName });
          return acc;
        }, []),
    [dayCards]
  );
  const canPlan = Boolean(trip?.startDate && trip?.endDate);
  const sub = user?.sub ?? '';
  const canEditTrip = trip ? effectiveCanEdit(trip, sub) : false;
  const showShare = trip ? canShowShareUI(trip, sub) : false;
  const attachmentsOk = trip ? canOpenAttachments(trip, sub) : true;
  const title = trip?.title?.trim() || tripDetailEn.untitled;
  const handleSharePdf = async (): Promise<void> => {
    if (!trip || pdfBusy) return;
    setPdfBusy(true);
    try {
      const model = buildItineraryPdfModel(trip, {
        weatherFor: (cityId, dateYmd) => {
          const live = weatherByKey[`${cityId}_${dateYmd}`];
          if (live) {
            return { highTempC: live.highTempC, lowTempC: live.lowTempC };
          }
          if (!user?.sub) return null;
          const cached = findDailyInCache(getWeatherCache(user.sub, cityId), dateYmd);
          if (!cached) return null;
          return { highTempC: cached.highTempC, lowTempC: cached.lowTempC };
        },
      });
      const blob = renderItineraryPdf(model);
      await shareOrDownloadItineraryPdf(blob, model.fileName, model.title);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      message.error(tripDetailEn.sharePdfFailed);
    } finally {
      setPdfBusy(false);
    }
  };
  const headerStart = formatTripHeaderDate(trip?.startDate ?? null);
  const headerEnd = formatTripHeaderDate(trip?.endDate ?? null);
  const headerDate =
    headerStart && headerEnd && headerStart !== headerEnd
      ? `${headerStart} — ${headerEnd}`
      : headerStart ?? headerEnd;

  if (loading || isDataSyncing) {
    return (
      <div className={styles.tripDetail}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className={styles.tripDetail}>
        <div className={styles.container}>
          <p>Trip not found.</p>
          <Button type="primary" onClick={() => navigate('/trips', { replace: true })}>
            {tripDetailEn.back}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tripDetail}>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <Button
            type="link"
            className={`${styles.backLink} ${styles.noPrint}`}
            onClick={() => navigate('/trips', { replace: true })}
          >
            <ArrowLeftOutlined /> {tripDetailEn.back}
          </Button>
          <div className={`${styles.topBarActions} ${styles.noPrint}`}>
            {dayCards.length > 0 ? (
              <Button
                icon={<FilePdfOutlined />}
                loading={pdfBusy}
                onClick={() => void handleSharePdf()}
                aria-label={tripDetailEn.sharePdf}
              >
                {tripDetailEn.sharePdf}
              </Button>
            ) : null}
            {showShare ? (
              <Button icon={<TeamOutlined />} onClick={() => setShareOpen(true)}>
                {tripShareEn.shareTrip}
              </Button>
            ) : null}
          </div>
        </div>

        <div className={styles.stack}>
          {!canEditTrip ? (
            <Alert type="info" showIcon message={tripShareEn.readOnlyBanner} />
          ) : null}
          {!attachmentsOk ? (
            <Alert type="warning" showIcon message={tripShareEn.attachmentOwnerOnly} />
          ) : null}
          <TripChecklistDrawer
            trip={trip}
            onToggleTask={canEditTrip ? handleToggleTask : () => undefined}
            onDeleteTask={canEditTrip ? handleDeleteTask : () => undefined}
            onAddTask={canEditTrip ? handleAddTask : () => undefined}
          />

          {trip.startDate ? (
            <CruiseCountdownCard
              tripName={title}
              tripType={trip.tripType}
              startDate={trip.startDate}
              showsHeader={false}
            />
          ) : null}

          <h1 className={styles.tripTitle}>{tripDetailEn.tripHeader(title, headerDate)}</h1>

          {visibleDayCards.length > 0 ? (
            <div className={styles.weatherRefreshRow}>
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                loading={weatherLoading}
                onClick={() => void loadWeatherForCards(true)}
              >
                {weatherEn.refresh}
              </Button>
            </div>
          ) : null}

          {dayCards.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>{tripDetailEn.noDays}</div>
              <p className={styles.emptyDescription}>{tripDetailEn.noDaysDescription}</p>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => navigate(`${tripHref}/edit`)}
              >
                {tripDetailEn.addCity}
              </Button>
            </div>
          ) : visibleDayCards.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>{tripDetailEn.noDays}</div>
            </div>
          ) : (
            <div className={`${styles.dayList} ${styles.printDayList}`}>
              {visibleDayCards.map((card) => {
                const stayDays = enumerateStayDays(card.arrivalDate, card.departureDate);
                const weatherByDate: Record<string, ForecastDay | null> = {};
                let anyPending = false;
                for (const ymd of stayDays) {
                  const key = `${card.cityId}_${ymd}`;
                  if (key in weatherByKey) {
                    weatherByDate[ymd] = weatherByKey[key];
                  } else if (!card.isAtSea) {
                    anyPending = true;
                  }
                }
                return (
                  <CityVisitCard
                    key={card.cityId}
                    tripId={tripHref.slice('/trips/'.length)}
                    card={card}
                    trip={trip}
                    showPlanIcons={showPlanIcons}
                    canPlan={canPlan}
                    weatherByDate={weatherByDate}
                    temperatureUnit={temperatureUnit}
                    weatherLoading={weatherLoading && anyPending}
                    hidePastDays={hidePastDays}
                  />
                );
              })}
            </div>
          )}

          {dayCards.length > 0 ? (
            <NotesAttachmentsCard
              trip={trip}
              cityOptions={attachmentCityOptions}
              defaultExpanded
              canEdit={canEditTrip}
              onChangeTrip={canEditTrip ? persist : () => undefined}
            />
          ) : null}
        </div>
      </div>

      <TripShareRoster
        open={shareOpen}
        tripId={trip.id}
        tripName={title}
        tripEndDate={trip.endDate}
        onClose={() => setShareOpen(false)}
      />

      <div className={styles.footer}>
        <div className={styles.footerActions}>
          <Button
            type="primary"
            className={styles.editButton}
            icon={<EditOutlined />}
            disabled={!canEditTrip}
            onClick={() => navigate(`${tripHref}/edit`)}
          >
            {tripDetailEn.editTrip}
          </Button>
          <Button
            className={styles.hotelsButton}
            icon={<AccommodationIcon size={16} />}
            disabled={!canEditTrip}
            onClick={() => {
              if (!canEditTrip) return;
              navigate(`${tripHref}/lodging`);
            }}
          >
            {tripDetailEn.hotels}
          </Button>
        </div>
        <div className={styles.footerMeta}>
          <div className={styles.planningToggle}>
            <span>{tripDetailEn.planning}</span>
            <Switch checked={showPlanIcons} onChange={handleShowPlanIconsChange} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripDetail;
